import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Activity,
  Crown,
  Image,
  FileText,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Loader2,
  ArrowRight,
  Settings,
  Coins
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { motion } from 'framer-motion';
import adminService, { DashboardStats, RecentUser, RecentActivity } from '../../services/adminService';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
}> = ({ title, value, change, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    whileHover={{ y: -2 }}
  >
    <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl overflow-hidden group">
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent dark:from-gray-700/20 dark:to-transparent" />
      <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold text-slate-600 dark:text-gray-400 uppercase tracking-wide">
          {title}
        </CardTitle>
        <div className={`p-3 rounded-xl ${color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </CardHeader>
      <CardContent className="relative">
        <div className="text-3xl font-bold text-slate-800 dark:text-gray-100 mb-2">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {change && (
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
              {change}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const AdminDashboardPage: React.FC = () => {
  // Fetch dashboard data
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ['admin-dashboard-stats'],
    queryFn: adminService.getDashboardStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: recentUsers, isLoading: usersLoading } = useQuery<RecentUser[]>({
    queryKey: ['admin-recent-users'],
    queryFn: () => adminService.getRecentUsers(5),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: recentActivity, isLoading: activityLoading } = useQuery<RecentActivity[]>({
    queryKey: ['admin-recent-activity'],
    queryFn: () => adminService.getRecentActivity(10),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (statsLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      <div className="container mx-auto p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 dark:border-gray-700/50 shadow-lg"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 dark:text-gray-400 mt-2 text-lg">
              Monitor your SaaS application performance and user activity
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-500 dark:text-gray-400 mb-1">Last updated</div>
            <div className="text-lg font-semibold text-slate-700 dark:text-gray-300">
              {new Date().toLocaleTimeString()}
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard
          title="Total Users"
          value={stats?.users.total || 0}
          change={`${stats?.users.new_this_month || 0} new this month`}
          icon={Users}
          color="bg-blue-500"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${stats?.revenue.monthly.toFixed(2) || '0.00'}`}
          change={`${stats?.users.paying || 0} paying users`}
          icon={DollarSign}
          color="bg-green-500"
        />
        <StatCard
          title="AI Generations"
          value={stats?.ai.total_generations || 0}
          icon={Zap}
          color="bg-purple-500"
        />
        <StatCard
          title="Active Tasks"
          value={stats?.ai.active_tasks || 0}
          icon={Activity}
          color="bg-orange-500"
        />
      </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard
          title="Active Users"
          value={stats?.users.active || 0}
          change="Last 7 days"
          icon={CheckCircle}
          color="bg-emerald-500"
        />
        <StatCard
          title="Total Posts"
          value={stats?.content.total_posts || 0}
          change={`${stats?.content.posts_this_month || 0} this month`}
          icon={FileText}
          color="bg-indigo-500"
        />
        <StatCard
          title="Media Files"
          value={stats?.content.total_media || 0}
          change={`${stats?.content.media_this_month || 0} this month`}
          icon={Image}
          color="bg-pink-500"
        />
        <StatCard
          title="Total Revenue"
          value={`$${stats?.revenue.total.toFixed(2) || '0.00'}`}
          change="Annual projection"
          icon={TrendingUp}
          color="bg-cyan-500"
        />
      </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Users */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />
              <CardHeader className="relative border-b border-slate-100 dark:border-gray-700">
                <CardTitle className="flex items-center space-x-3 text-slate-800 dark:text-gray-100">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xl font-semibold">Recent Users</span>
                </CardTitle>
              </CardHeader>
            <CardContent className="relative p-6">
              {usersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {recentUsers?.map((user, index) => (
                    <motion.div 
                      key={user.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-100 dark:border-gray-600 hover:shadow-md hover:bg-slate-50 dark:hover:bg-gray-700 transition-all duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 dark:text-gray-100">{user.name}</p>
                          <p className="text-sm text-slate-600 dark:text-gray-400">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
                          user.tier === 'Enterprise' ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 dark:from-purple-900/50 dark:to-purple-800/50 dark:text-purple-200' :
                          user.tier === 'Pro' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900/50 dark:to-blue-800/50 dark:text-blue-200' :
                          'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 dark:from-gray-700/50 dark:to-gray-600/50 dark:text-gray-200'
                        }`}>
                          {user.tier}
                        </span>
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-2 font-medium">{user.joined_at}</p>
                      </div>
                    </motion.div>
                  )) || <p className="text-slate-500 dark:text-gray-400 text-center py-8 italic">No recent users</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-blue-500/5" />
              <CardHeader className="relative border-b border-slate-100 dark:border-gray-700">
                <CardTitle className="flex items-center space-x-3 text-slate-800 dark:text-gray-100">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <Activity className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xl font-semibold">Recent Activity</span>
                </CardTitle>
              </CardHeader>
            <CardContent className="relative p-6">
              {activityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-green-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity?.map((activity, index) => (
                    <motion.div 
                      key={activity.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-start space-x-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <div className={`w-3 h-3 rounded-full mt-2 shadow-sm ${
                        activity.type === 'user' ? 'bg-blue-500' :
                        activity.type === 'ai' ? 'bg-purple-500' :
                        activity.type === 'content' ? 'bg-green-500' :
                        'bg-slate-400'
                      }`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-gray-100 mb-1">
                          {activity.action}
                        </p>
                        <div className="flex items-center space-x-2 text-xs">
                          <span className="text-slate-600 dark:text-gray-400 font-medium">{activity.user}</span>
                          <span className="text-slate-400 dark:text-gray-500">•</span>
                          <span className="text-slate-500 dark:text-gray-400">{activity.time_display}</span>
                        </div>
                      </div>
                    </motion.div>
                  )) || <p className="text-slate-500 dark:text-gray-400 text-center py-8 italic">No recent activity</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

        {/* Modern Admin Tools */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-pink-500/5" />
            <CardHeader className="relative border-b border-slate-100 dark:border-gray-700">
              <CardTitle className="flex items-center space-x-3 text-slate-800 dark:text-gray-100">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Settings className="h-4 w-4 text-white" />
                </div>
                <span className="text-xl font-bold">Administration Tools</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="relative p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/app/admin/tiers" className="group">
                  <motion.div 
                    whileHover={{ x: 4 }}
                    className="flex items-center justify-center space-x-4 p-4 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-xl hover:from-slate-100 hover:to-blue-100 dark:hover:from-gray-700 dark:hover:to-blue-800/30 transition-all duration-300 border border-slate-200/50 dark:border-gray-600/50 hover:border-blue-200 dark:hover:border-blue-700 hover:shadow-lg text-center"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                      <Crown className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 dark:text-gray-100">Subscription Tiers</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-400">Configure pricing plans and limits</p>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </motion.div>
                </Link>

                <Link to="/app/admin/users" className="group">
                  <motion.div 
                    whileHover={{ x: 4 }}
                    className="flex items-center justify-center space-x-4 p-4 bg-gradient-to-r from-slate-50 to-emerald-50 dark:from-gray-800 dark:to-emerald-900/20 rounded-xl hover:from-slate-100 hover:to-emerald-100 dark:hover:from-gray-700 dark:hover:to-emerald-800/30 transition-all duration-300 border border-slate-200/50 dark:border-gray-600/50 hover:border-emerald-200 dark:hover:border-emerald-700 hover:shadow-lg text-center"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 dark:text-gray-100">User Management</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-400">Manage users, credits, and permissions</p>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </motion.div>
                </Link>



                <Link to="/app/admin/ai-task-costs" className="group">
                  <motion.div 
                    whileHover={{ x: 4 }}
                    className="flex items-center justify-center space-x-4 p-4 bg-gradient-to-r from-slate-50 to-amber-50 dark:from-gray-800 dark:to-amber-900/20 rounded-xl hover:from-slate-100 hover:to-amber-100 dark:hover:from-gray-700 dark:hover:to-amber-800/30 transition-all duration-300 border border-slate-200/50 dark:border-gray-600/50 hover:border-amber-200 dark:hover:border-amber-700 hover:shadow-lg text-center"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                      <DollarSign className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 dark:text-gray-100">AI Service Costs</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-400">Set credit costs for AI features</p>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </motion.div>
                </Link>

                <Link to="/app/admin/stripe-sync" className="group">
                  <motion.div 
                    whileHover={{ x: 4 }}
                    className="flex items-center justify-center space-x-4 p-4 bg-gradient-to-r from-slate-50 to-purple-50 dark:from-gray-800 dark:to-purple-900/20 rounded-xl hover:from-slate-100 hover:to-purple-100 dark:hover:from-gray-700 dark:hover:to-purple-800/30 transition-all duration-300 border border-slate-200/50 dark:border-gray-600/50 hover:border-purple-200 dark:hover:border-purple-700 hover:shadow-lg text-center"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                      <Coins className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 dark:text-gray-100">Stripe Sync</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-400">Sync tiers and packages with Stripe</p>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </motion.div>
                </Link>

                <motion.button 
                  whileHover={{ x: 4 }}
                  className="group flex items-center justify-center space-x-4 p-4 bg-gradient-to-r from-slate-50 to-orange-50 dark:from-gray-800 dark:to-orange-900/20 rounded-xl hover:from-slate-100 hover:to-orange-100 dark:hover:from-gray-700 dark:hover:to-orange-800/30 transition-all duration-300 border border-slate-200/50 dark:border-gray-600/50 hover:border-orange-200 dark:hover:border-orange-700 hover:shadow-lg text-center"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                    <Activity className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 dark:text-gray-100">System Monitor</h3>
                    <p className="text-sm text-slate-600 dark:text-gray-400">View system health and metrics</p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                </motion.button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;